import axios from 'axios';
import { firebaseAuth } from '@/lib/firebase';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Generate or retrieve a persistent device ID for analytics
function getDeviceId(): string {
  const key = 'bg_device_id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
}

// Convert snake_case keys to camelCase recursively
function snakeToCamel(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    return obj.map(snakeToCamel);
  }
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>).map(([key, value]) => [
        key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase()),
        snakeToCamel(value),
      ])
    );
  }
  return obj;
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - attach Firebase ID token and analytics headers
apiClient.interceptors.request.use(
  async (config) => {
    const token = await firebaseAuth.getIdToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['X-Device-Id'] = getDeviceId();
    config.headers['X-Platform'] = 'web';
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - transform snake_case to camelCase and handle errors
apiClient.interceptors.response.use(
  (response) => {
    // Transform response data from snake_case to camelCase
    if (response.data) {
      response.data = snakeToCamel(response.data);
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - could trigger re-auth here
      console.warn('Unauthorized request - token may be expired');
    }
    return Promise.reject(error);
  }
);
